import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Multi-select with checkbox list inside a Popover.
 * options: [{value, label}]
 * value: array of selected values
 * onChange: (newValue[]) => void
 */
export default function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = "Select…",
  testIdPrefix = "multi-select",
  className,
  emptyMessage = "No options",
}) {
  const [open, setOpen] = useState(false);
  const valueSet = new Set(value);

  const toggle = (v) => {
    if (valueSet.has(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };

  const remove = (v, e) => {
    e?.stopPropagation();
    onChange(value.filter((x) => x !== v));
  };

  const selected = options.filter((o) => valueSet.has(o.value));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal h-auto min-h-10 py-2 px-3",
            className
          )}
          data-testid={`${testIdPrefix}-trigger`}
        >
          <div className="flex flex-wrap gap-1 items-center min-h-6">
            {selected.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              selected.map((o) => (
                <Badge
                  key={o.value}
                  variant="secondary"
                  className="rounded-none gap-1 pr-1"
                  data-testid={`${testIdPrefix}-chip-${o.value}`}
                >
                  {o.label}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => remove(o.value, e)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") remove(o.value, e);
                    }}
                    className="ml-0.5 inline-flex items-center justify-center rounded-sm hover:bg-muted-foreground/20 p-0.5 cursor-pointer"
                    aria-label={`Remove ${o.label}`}
                  >
                    <X className="h-3 w-3" />
                  </span>
                </Badge>
              ))
            )}
          </div>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] p-0 max-h-72 overflow-y-auto"
        data-testid={`${testIdPrefix}-popover`}
      >
        {options.length === 0 ? (
          <div className="px-3 py-6 text-sm text-muted-foreground text-center">
            {emptyMessage}
          </div>
        ) : (
          <div className="py-1">
            {options.map((o) => {
              const checked = valueSet.has(o.value);
              return (
                <label
                  key={o.value}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50",
                    checked && "bg-muted/30"
                  )}
                  data-testid={`${testIdPrefix}-option-${o.value}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(o.value)}
                  />
                  <span className="text-sm flex-1">{o.label}</span>
                </label>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
